import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import Footer from '../../common/layout/Footer';
import { PRIVACY_POLICY_CONFIG } from '../../constants/privacyPolicy';
import './PublicOfferPage.css';

const PublicOfferPage: React.FC = () => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  React.useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollableDistance = documentHeight - windowHeight;
      
      // Показуємо кнопку тільки коли користувач доскролив вниз (в кінці сторінки)
      const nearEnd = position >= scrollableDistance - 100;
      setShowScrollButton(nearEnd && scrollableDistance > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollClick = () => {
    // Завжди скролимо вверх до початку
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTocClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Функція для рендерингу тексту з активними посиланнями на email, phone та website
  const renderTextWithLinks = (text: string): (string | React.ReactElement)[] => {
    const emailRegex = /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/gi;
    const phoneRegex = /\+\d{1,3}\s?[\(\d{1,4}\)]*\s?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{1,9}/g;
    const websiteRegex = /onvisit\.pro(?:\/privacy-policy)?(?![/])/gi;

    // Обробляємо всі типи посилань в одному проході для кожного рядка тексту
    if (typeof text !== 'string') return [text];

    const result: (string | React.ReactElement)[] = [];
    let keyCounter = 0;

    // Знаходимо всі збіги для всіх типів посилань
    const allMatches: Array<{
      index: number;
      length: number;
      type: 'email' | 'phone' | 'website';
      text: string;
      href?: string;
    }> = [];

    // Reset regex lastIndex
    emailRegex.lastIndex = 0;
    phoneRegex.lastIndex = 0;
    websiteRegex.lastIndex = 0;

    // Email matches
    let emailMatch;
    while ((emailMatch = emailRegex.exec(text)) !== null) {
      allMatches.push({
        index: emailMatch.index,
        length: emailMatch[0].length,
        type: 'email',
        text: emailMatch[0],
        href: `mailto:${emailMatch[0]}`
      });
    }

    // Phone matches
    let phoneMatch;
    while ((phoneMatch = phoneRegex.exec(text)) !== null) {
      allMatches.push({
        index: phoneMatch.index,
        length: phoneMatch[0].length,
        type: 'phone',
        text: phoneMatch[0],
        href: `tel:${phoneMatch[0]}`
      });
    }

    // Website matches
    let websiteMatch;
    while ((websiteMatch = websiteRegex.exec(text)) !== null) {
      allMatches.push({
        index: websiteMatch.index,
        length: websiteMatch[0].length,
        type: 'website',
        text: websiteMatch[0],
        href: websiteMatch[0].includes('/privacy-policy')
          ? '/privacy-policy'
          : `https://${PRIVACY_POLICY_CONFIG.WEBSITE_URL}`
      });
    }

    // Сортуємо за позицією в тексті
    allMatches.sort((a, b) => a.index - b.index);

    // Видаляємо дублікати (якщо є перекриття)
    const filteredMatches = allMatches.filter((match, index, arr) => {
      return index === 0 || match.index >= arr[index - 1].index + arr[index - 1].length;
    });

    // Будуємо результат
    let lastIndex = 0;
    filteredMatches.forEach((match) => {
      // Додаємо текст перед збігом
      if (match.index > lastIndex) {
        result.push(text.substring(lastIndex, match.index));
      }

      // Додаємо посилання
      const linkStyle = match.type === 'website'
        ? { color: colors.primary, textDecoration: 'none', fontWeight: 'bold' }
        : { color: colors.primary, textDecoration: 'underline' };

      const linkProps = match.type === 'website' && match.href && !match.href.startsWith('/')
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {};

      result.push(
        <a
          key={`${match.type}-${keyCounter++}`}
          href={match.href}
          style={linkStyle}
          {...linkProps}
        >
          {match.text}
        </a>
      );

      lastIndex = match.index + match.length;
    });

    // Додаємо залишок тексту
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }

    return result.length > 0 ? result : [text];
  };

  const content = {
    ua: {
      title: 'Публічна оферта про надання послуг',

      intro: {
        heading: 'ДОГОВІР-ОФЕРТА ПРО НАДАННЯ ПОСЛУГ',
        text: `Цей документ є відкритою пропозицією (офертою) Виконавця про укладання Договору про надання програмного забезпечення як послуги на SaaS платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} на викладених у даній Оферті умовах.`,
      },

      sections: [
        {
          id: 'terms-definitions',
          heading: 'Терміни та визначення',
          content: [
        {
          id: 'claims-procedure',
          heading: '8. Claims Procedure',
          content: [
            {
              text: '8.1. All disputes and disagreements, the Parties try to resolve through negotiations.',
            },
            {
              text: `8.2. The claim is sent in writing to the Executor's contact email: ${PRIVACY_POLICY_CONFIG.EMAIL}.`,
            },
            {
              text: '8.3. The period for consideration of the claim is 30 calendar days from the moment of its receipt.',
            },
            {
              text: '8.4. If the dispute cannot be resolved through negotiations, it is resolved in court in accordance with the legislation of Ukraine.',
            },
          ],
        },
        {
          id: 'executor-data',
          heading: '9. Executor Data',
          content: [
            {
              text: `Private Enterprise "Internet Agency "New Media Space"
Location: 76018, Ivano-Frankivsk, str. Huzara Liubomyra Kardinala, 24B, office 14
EDRPOU code 36498362

Contact information: ${PRIVACY_POLICY_CONFIG.EMAIL}`,
            },
          ],
        },
        {
          id: 'term-termination',
          heading: '10. Term of the Agreement and Termination Conditions',
          content: [
            {
              text: '10.1. The Agreement comes into force from the moment of Acceptance and is valid until the full performance of obligations by the parties.',
            },
            {
              text: '10.2. The Customer has the right at any time to stop using the Services by deleting their account.',
            },
            {
              text: '10.3. The Executor has the right to terminate the Agreement unilaterally out of court in case of gross violation by the Customer of the terms of the Agreement (p. 4.2), having warned the Customer 3 days in advance.',
            },
          ],
        },
            {
              text: 'Облікові дані - унікальна комбінація електронної адреси (або номера телефону) та пароля, що використовується Замовником для доступу до Особистого кабінету.',
            },
          ],
        },
        {
          id: 'general-provisions',
          heading: '1. Загальні положення',
          content: [
            {
              text: `1.1. Цей документ є відкритою пропозицією (офертою) Виконавця, адресованою невизначеному колу осіб, укласти Договір про надання програмного забезпечення як послуги на SaaS платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} (надалі – Договір) на викладених у даній Оферті умовах.`,
            },
            {
              text: '1.2. Виконавцем згідно Договору є один із суб\'єктів господарювання, вказаний в рахунках чи інших документах на оплату.',
            },
            {
              text: `1.3. Відповідно до ст. ст. 633, 638, 641 Цивільного кодексу України, цей Договір є публічним договором і в разі прийняття викладених в Оферті умов (акцепту), фізична або юридична особа стає Замовником за Договором і зобов\'язується виконувати умови цього Договору та додатків до нього, які є його невід\'ємною частиною. Договір укладається шляхом прийняття (акцепту) Оферти Замовником, яка вважається прийнятою у випадку реєстрації Замовником на SaaS платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} та/або здійснення Замовником оплати за інформаційні послуги на SaaS платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}.`,
            },
            {
              text: '1.4. Замовником не може бути особа, щодо якої, у відповідності із законодавством України, існують обмеження щодо укладення Договору (в тому числі, але не виключно, особи, щодо яких застосовані відповідні санкції згідно із Законом України "Про санкції" та/або існують інші обмеження на ведення господарської діяльності, включаючи обмеження, пов\'язані з місцезнаходженням таких осіб на тимчасово окупованій території України).',
            },
            {
              text: `1.5. Виконавець залишає за собою право вносити зміни в умови Оферти (в тому числі у ціну та описання тарифів) та/або відкликати Оферту в будь-який момент на свій розсуд. Зміни, що стосуються тарифів та умов надання вже оплачених послуг, не застосовуються до поточного розрахункового періоду Замовника. Про суттєві зміни в Оферті Замовник буде сповіщений електронною поштою або внутрішнім сповіщенням в особистому кабінеті не пізніше, ніж за 10 (десять) днів до їх набрання чинності. У випадку внесення Виконавцем змін до Оферти, такі зміни вступають в силу з моменту розміщення зміненого тексту Оферти в мережі Інтернет на SaaS  платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}, якщо інше не зазначено при такому розміщенні.`,
            },
          ],
        },
        {
          id: 'subject-matter',
          heading: '2. Предмет Договору',
          content: [
            {
              text: `2.1. Виконавець бере на себе обов\'язок, за дорученням Замовника, надати інформаційні послуги, зазначені в актуальному Переліку послуг та Тарифних планах, розміщених за посиланням: ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}/subscription, які є невід'ємною частиною цього Договору, а Замовник бере на себе обов\'язок прийняти такі послуги, та оплатити їх у порядку та на умовах, які визначені цим Договором. Надання Виконавцем послуг, що не зазначені в Переліку послуг та Тарифних планах на SaaS  платформі ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} (надалі – SaaS  платформа), здійснюється у рамках окремого договору, який укладається Виконавцем у простій письмовій формі.`,
            },
            {
              text: '2.2. Конкретний перелік програмного забезпечення як послуги, які Виконавець надає Замовнику, може зазначатись у рахунку, що виставляється Виконавцем. При необхідності у рахунку зазначаються додаткові умови надання послуг (особливий порядок надання, кількісні та якісні показники тощо).',
            },
            {
              text: '2.3. Виключні права на програмне забезпечення, архітектуру, дизайн інтерфейсу та унікальні технологічні рішення SaaS платформи, а також на будь-які об\'єкти інтелектуальної власності, які виникли або можуть виникнути у рамках надання Послуг за Договором, в повному обсязі та на весь строк дії авторського права належать Виконавцю, конкретне найменування якого вказане в рахунку чи інших документах на оплату.',
            },
            {
              text: '2.4. Замовник залишає за собою всі права на об\'єкти інтелектуальної власності, які він надає для розміщення на SaaS платформі (тексти, зображення, товарні знаки тощо). Надаючи матеріали, Замовник надає Виконавцю невиключну безоплатну ліцензію на їх використання в цілях надання Послуг за цим Договором.',
            },
          ],
        },
        {
          id: 'payment',
          heading: '3. Оплата послуг',
          content: [
            {
              text: '3.1. Оплата послуг здійснюється в українських гривнях у безготівковому порядку за допомогою: сервісу Online платежу Liqpay (https://www.liqpay.ua) або платежу на розрахунковий рахунок.',
            },
            {
              text: '3.2. У випадку оплати за допомогою сервісу Online платежу Liqpay Замовник перенаправляється на платіжний сервіс LiqPay, де Замовник має змогу сплатити за допомогою карток платіжних систем Visa та Mastercard. В цьому випадку оплата додатково регулюється правилами платіжного сервісу, банку (в тому числі банку емітента картки Замовника) та інших учасників платежів. Виконавець не здійснює обробку операцій по банківським карткам. У разі виникнення помилок або відмов в оплаті Замовник має звернутися до банку емітенту картки або до представника платіжної системи, через яку здійснювалась оплата.',
            },
            {
              text: '3.3. Замовник самостійно несе повну відповідальність за надання помилкових або неправдивих відомостей, що спричинило неможливість належного виконання Виконавцем своїх зобов\'язань перед Замовником. Будь-яка оплата, здійснена від імені Замовника з використанням персональних даних та платіжних засобів Замовника, вважається здійсненою самим Замовником.',
            },
            {
              text: '3.4. Ціна послуг не містить в собі комісію і додаткові збори банків та платіжних систем. Замовник самостійно і своїм коштом сплачує такі збори. Розмір комісії або додаткового збору повідомляє відповідний банк та платіжна система.',
            },
          ],
        },
        {
          id: 'refund',
          heading: '4. Повернення оплати',
          content: [
            {
              text: '4.1.1. У разі, якщо Замовником є фізична особа-споживач, на нього поширюються права, передбачені Законом України «Про захист прав споживачів».',
            },
            {
              text: '4.1.2. Для Замовників-суб\'єктів господарювання та фізичних осіб-підприємців діють такі умови: невикористання Замовником оплачених послуг, відмова від одержання оплачених послуг (в т.ч. шляхом скасування реєстрації на SaaS платформі), зміна чи видалення інформації про Замовника, неодержання Замовником бажаних результатів за наслідками надання послуг, не є підставою для відмови від Договору та підставою для повернення коштів.',
            },
            {
              text: '4.2. Виконавець має право заблокувати Замовнику доступ до SaaS платформи у разі порушення Договору та/або якщо дії Замовника є шахрайськими, незаконними, спрямовані на нанесення шкоди SaaS платформі, організації DDOS або фішингової атаки. В такому випадку оплата за інформаційні послуги поверненню не підлягає.',
            },
          ],
        },
        {
          id: 'liability',
          heading: '5. Відповідальність',
          content: [
            {
              text: '5.1. Виконавець не несе відповідальність за зміст інформаційних матеріалів, які надаються Замовником, так само як і за зміст інформації, що розташована на технічних та інформаційних ресурсах Замовника.',
            },
            {
              text: '5.2. Виконавець не несе відповідальність і не дає будь-яких явних або неявних гарантій (в тому числі гарантій додержання прав або придатності для конкретних цілей) на будь-яку інформацію, товар (роботи, послуги), які реалізує Замовник.',
            },
            {
              text: '5.3. Сторони цього Договору безумовно погоджуються з тим, що максимальний розмір збитків, який може бути стягнутий з Виконавця, обмежується сумою сплачених Замовником послуг, невиконання (неналежне виконання) яких призвело до заподіяння шкоди.',
            },
            {
              text: '5.4. Виконавець звільняється від відповідальності за невиконання (неналежне виконання) своїх зобов\'язань за цим Договором у випадку, якщо таке невиконання (неналежне виконання) пов\'язане з наступними обставинами:',
              list: [
                '5.4.1. Заміна, ремонт та налаштування обладнання, програмного забезпечення або проведення інших робіт, які викликані необхідністю підтримки працездатності або вдосконалення програмно-технічних засобів Виконавця;',
                '5.4.2. Дія (бездіяльність) третіх осіб; непрацездатність телекомунікаційних каналів, мереж передачі даних, інформаційних ресурсів або сервісів; аварії (збої в роботі) у силових електричних або комп\'ютерних мережах, що знаходяться за межами власних ресурсів Виконавця, або на функціонування яких Виконавець не має впливу; помилки, дефекти або збої сторонніх апаратного та програмного забезпечення, та веб-ресурсів, до яких Замовник має змогу перейти з SaaS платформи, в тому числі платіжної системи;',
                '5.4.3. Наявність помилок або шкідливих компонентів у програмному забезпеченні, яке використовується на серверах Виконавця або інших серверах мережі Інтернет, рівнозначно як у програмному забезпеченні, яке використовує Замовник;',
                '5.4.4. Недодержання Замовником конфіденційності своїх облікових даних або іншої інформації закритого характеру, несанкціонований доступ третіх осіб до технічних або інформаційних ресурсів Замовника;',
                '5.4.5. Наявність у пристроях користувачів SaaS платформи налаштувань і/або програм, що обмежують показ рекламних матеріалів Замовника, які розміщені на SaaS платформі (блокувальники реклами тощо);',
                '5.4.6. Розходження зовнішнього виду повної та адаптивної мобільної версії SaaS платформи Виконавця при перегляді на різних пристроях.',
              ],
            },
            {
              text: '5.5. Замовник самостійно несе повну відповідальність за будь-які дії, що він вживає у процесі використання мережі Інтернет, інформаційних ресурсів або послуг Виконавця, а також за наслідки таких дій.',
            },
            {
              text: '5.6. Замовник самостійно несе повну відповідальність за будь-які, в тому числі і несанкціоновані, дії третіх осіб, що мали місце внаслідок недодержання Замовником умов цього Договору про конфіденційність своїх облікових даних або іншої інформації закритого характеру, а також за наслідки таких дій.',
            },
            {
              text: '5.7. Замовник, роблячи акцепт цієї оферти, усвідомлює, що разом з рекламно-інформаційними матеріалами може передавати Виконавцю для розміщення на SaaS платформі об\'єкти інтелектуальної власності (засоби індивідуалізації та інше), при цьому Замовник самостійно несе повну відповідальність за можливі порушення законодавства про авторське право (суміжні права) та інші об\'єкти інтелектуальної власності, а також інші норми законодавства України, що пов\'язані з фактом розміщення у мережі Інтернет на SaaS платформі рекламно-інформаційних матеріалів, наданих Замовником.',
            },
            {
              text: '5.8. Замовник відшкодовує Виконавцю всі збитки, що було завдано у зв\'язку з розміщенням рекламно-інформаційних матеріалів Замовника, які не відповідають законодавству України.',
            },
          ],
        },
        {
          id: 'confidentiality',
          heading: '6. Конфіденційність',
          content: [
            {
              text: `6.1. Виконавець збирає та обробляє персональну інформацію, яку Замовник надає самостійно під час реєстрації або оформлення замовлення відповідно до Закону України «Про захист персональних даних», Договору та Політики конфіденційності SaaS платформи ${PRIVACY_POLICY_CONFIG.WEBSITE_URL}/privacy-policy.`,
            },
            {
              text: '6.2. Виконавець має право на передачу персональних даних Замовника третім особам, якщо така передача вимагається для виконання Виконавцем своїх обов\'язків за Договором та захисту прав та свобод Замовника або третіх осіб, якщо Замовник порушує умови Договору.',
            },
          ],
        },
        {
          id: 'warranties',
          heading: '7. Гарантії',
          content: [
            {
              text: '7.1. Виконавець гарантує, що володіє всіма правами та повноваженнями, необхідними для укладання та виконання цього Договору.',
            },
            {
              text: '7.2. Погоджуючись з умовами та приймаючи умови цієї Оферти шляхом її Акцепту, Замовник запевняє Виконавця та гарантує Виконавцю, що:',
              list: [
                '7.2.1. Замовник надав достовірні дані при реєстрації в якості користувача на SaaS платформі та при оформленні платіжних документів на оплату послуг;',
                '7.2.2. Замовник укладає цей Договір добровільно, при цьому Замовник: а) повністю ознайомився з умовами Оферти, б) повністю розуміє предмет Договору (Оферти);',
                '7.2.3. Замовник володіє всіма правами та повноваженнями, необхідними для укладання та виконання Договору;',
                '7.2.4. Замовник володіє всіма правами інтелектуальної власності щодо об\'єктів, які ним розміщуються на SaaS платформі (знаки для товарів і послуг, об\'єкти авторського права, промислові зразки, тощо).',
              ],
            },
          ],
        },
      ],
    },
    en: {
      title: 'Public Offer for Services',

      intro: {
        heading: 'AGREEMENT-OFFER FOR THE PROVISION OF SERVICES',
        text: `This document is an open proposal (offer) of the Executor regarding the conclusion of an Agreement for the provision of advertising and information services on the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} on the terms set out in this Offer.`,
      },

      sections: [
        {
          id: 'terms-definitions',
          heading: 'Terms and Definitions',
          content: [
            {
              text: 'In this Offer, the terms listed below are used in the following meaning:',
            },
            {
              text: 'SaaS Platform (Platform) - software provided as a service via the Internet, designed for business management, client appointments and service provision.',
            },
            {
              text: 'Services - advertising and information services provided by the Executor to the Customer through the SaaS Platform.',
            },
            {
              text: 'Personal Account - a protected part of the SaaS Platform available to the Customer after authorization, intended for managing account credentials, viewing services and their payment.',
            },
            {
              text: 'Tariff Plan - a set of conditions for providing Services that includes cost, volume of services and validity period.',
            },
            {
              text: 'Account Credentials - a unique combination of email address (or phone number) and password used by the Customer to access the Personal Account.',
            },
          ],
        },
        {
          id: 'general-provisions',
          heading: '1. General Provisions',
          content: [
            {
              text: `1.1. This document is an open proposal (offer) of the Executor addressed to an indefinite circle of persons to enter into an Agreement for the provision of advertising and information services on the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} (hereinafter – Agreement) on the terms set out in this Offer.`,
            },
            {
              text: '1.2. The Executor under the Agreement is one of the business entities specified in invoices or other payment documents.',
            },
            {
              text: `1.3. In accordance with Art. 633, 638, 641 of the Civil Code of Ukraine, this Agreement is a public contract and in case of acceptance of the terms set forth in the Offer (acceptance), a natural or legal person becomes the Customer under the Agreement and undertakes to comply with the terms of this Agreement and its annexes, which are an integral part thereof. The Agreement is concluded by accepting (accepting) the Offer by the Customer, which is considered accepted in the event of the Customer\'s registration on the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} and/or the Customer\'s payment for advertising and information services on the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}.`,
            },
            {
              text: '1.4. The Customer cannot be a person with respect to whom, in accordance with the legislation of Ukraine, there are restrictions on concluding the Agreement (including, but not limited to, persons to whom appropriate sanctions have been applied in accordance with the Law of Ukraine "On Sanctions" and/or there are other restrictions on business activities, including restrictions related to the location of such persons in the temporarily occupied territory of Ukraine).',
            },
            {
              text: `1.5. The Executor reserves the right to make changes to the terms of the Offer (including price and tariff descriptions) and/or withdraw the Offer at any time at its discretion. Changes regarding subscription and conditions of already paid services do not apply to the current billing period of the Customer. About significant changes in the Offer, the Customer will be notified by email or internal notification in the personal account no later than 10 (ten) days before they take effect. In case of changes to the Offer by the Executor, such changes take effect from the moment the changed text of the Offer is posted on the Internet on the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}, unless otherwise specified when posting.`,
            },
          ],
        },
        {
          id: 'subject-matter',
          heading: '2. Subject Matter',
          content: [
            {
              text: `2.1. The Executor undertakes, at the request of the Customer, to provide advertising and information services specified in the current List of Services and Tariff Plans located at the link: ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME}/subscription, which are an integral part of this Agreement, and the Customer undertakes to accept such services and pay for them in the manner and on the terms determined by this Agreement. The provision by the Executor of services not specified in the List of Services and Tariff Plans on the SaaS platform ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} (hereinafter – SaaS platform) is carried out under a separate agreement concluded by the Executor in simple written form.`,
            },
            {
              text: '2.2. The specific list of advertising and information services provided by the Executor to the Customer may be indicated in the invoice issued by the Executor. If necessary, the invoice specifies additional conditions for providing services (special procedure, quantitative and qualitative indicators, etc.).',
            },
            {
              text: '2.3. Exclusive rights to software, architecture, interface design and unique technological solutions of the SaaS platform, as well as to any intellectual property objects that have arisen or may arise in the course of providing Services under the Agreement, in full and for the entire duration of copyright, belong to the Executor, the specific name of which is specified in the invoice or other payment documents.',
            },
            {
              text: '2.4. The Customer retains all rights to intellectual property objects that he provides for placement on the SaaS platform (texts, images, trademarks, etc.). By providing materials, the Customer grants the Executor a non-exclusive free license to use them for the purpose of providing Services under this Agreement.',
            },
          ],
        },
        {
          id: 'payment',
          heading: '3. Payment for Services',
          content: [
            {
              text: '3.1. Payment for services is made in Ukrainian hryvnia in non-cash form using: LiqPay online payment service (https://www.liqpay.ua) or payment to a current account.',
            },
            {
              text: '3.2. In case of payment using the LiqPay online payment service, the Customer is redirected to the LiqPay payment service, where the Customer can pay using payment system cards Visa and Mastercard. In this case, payment is additionally governed by the rules of the payment service, bank (including the bank issuing the Customer\'s card) and other participants in payments. The Executor does not process bank card transactions. In case of payment errors or rejections, the Customer should contact the card-issuing bank or the representative of the payment system through which the payment was made.',
            },
            {
              text: '3.3. The Customer independently bears full responsibility for providing incorrect or false information that prevented the proper performance by the Executor of its obligations to the Customer. Any payment made on behalf of the Customer using the Customer\'s personal data and payment instruments is considered made by the Customer himself.',
            },
            {
              text: '3.4. The price of services does not include commissions and additional charges from banks and payment systems. The Customer independently and at its own expense pays such charges. The amount of the commission or additional charge is reported by the appropriate bank and payment system.',
            },
          ],
        },
        {
          id: 'refund',
          heading: '4. Refund of Payment',
          content: [
            {
              text: '4.1.1. In case the Customer is an individual consumer, the rights provided by the Law of Ukraine "On Consumer Protection" apply to him.',
            },
            {
              text: '4.1.2. For Customers - business entities and individual entrepreneurs, the following conditions apply: non-use of paid services by the Customer, refusal to receive paid services (including by canceling registration on the SaaS platform), changing or deleting information about the Customer, Customer not receiving desired results as a result of service provision, is not a reason for refusing the Agreement and is not a reason for refund.',
            },
            {
              text: '4.2. The Executor has the right to block access to the SaaS platform for the Customer in case of breach of the Agreement and/or if the Customer\'s actions are fraudulent, illegal, aimed at causing harm to the SaaS platform, DDoS organization or phishing attacks. In this case, payment for advertising and information services is not subject to refund.',
            },
          ],
        },
        {
          id: 'liability',
          heading: '5. Liability',
          content: [
            {
              text: '5.1. The Executor is not responsible for the content of advertising and information materials provided by the Customer, nor for the content of information located on the Customer\'s technical and information resources.',
            },
            {
              text: '5.2. The Executor is not liable and does not provide any express or implied warranties (including warranties of rights compliance or fitness for specific purposes) for any information, goods (works, services) provided by the Customer.',
            },
            {
              text: '5.3. The parties to this Agreement unconditionally agree that the maximum amount of damages that can be recovered from the Executor is limited to the amount paid by the Customer for services, the non-performance (improper performance) of which caused the harm.',
            },
            {
              text: '5.4. The Executor is relieved of responsibility for non-performance (improper performance) of its obligations under this Agreement if such non-performance (improper performance) is related to the following circumstances:',
              list: [
                '5.4.1. Replacement, repair and configuration of equipment, software or other work necessitated by the need to maintain the performance or improve the software and technical means of the Executor;',
                '5.4.2. Actions (inactions) of third parties; failure of telecommunication channels, data networks, information resources or services; accidents (malfunctions) in power or computer networks outside the Executor\'s own resources or over which the Executor has no control; errors, defects or failures of third-party hardware and software, and web resources accessible from the Site, including payment systems;',
                '5.4.3. Presence of errors or malicious components in software used on the Executor\'s servers or other Internet servers, equally in software used by the Customer;',
                '5.4.4. Customer\'s non-compliance with confidentiality of their account credentials or other confidential information, unauthorized access by third parties to the Customer\'s technical or information resources;',
                '5.4.5. Presence of settings and/or programs on Site users\' devices that restrict the display of advertising materials of the Customer placed on the Site (ad blockers, etc.);',
                '5.4.6. Discrepancy between the appearance of the full and adaptive mobile version of the Executor\'s Site when viewed on different devices.',
              ],
            },
            {
              text: '5.5. The Customer independently bears full responsibility for any actions he takes in the process of using the Internet, information resources or the services of the Executor, as well as for the consequences of such actions.',
            },
            {
              text: '5.6. The Customer independently bears full responsibility for any, including unauthorized, actions of third parties that occurred as a result of the Customer\'s non-compliance with the terms of this Agreement regarding confidentiality of their account credentials or other confidential information, as well as for the consequences of such actions.',
            },
            {
              text: '5.7. The Customer, by accepting this offer, understands that together with advertising and information materials, may transfer to the Executor for placement on the Site intellectual property objects (means of individualization, etc.), while the Customer independently bears full responsibility for possible violations of copyright law (related rights) and other intellectual property objects, as well as other norms of Ukrainian legislation related to the placement on the Internet on the Site of advertising and information materials provided by the Customer.',
            },
            {
              text: '5.8. The Customer compensates the Executor for all damages caused in connection with the placement of advertising and information materials of the Customer that do not comply with Ukrainian legislation.',
            },
          ],
        },
        {
          id: 'confidentiality',
          heading: '6. Confidentiality',
          content: [
            {
              text: `6.1. The Executor collects and processes personal information that the Customer provides voluntarily during registration or order placement in accordance with the Law of Ukraine "On Personal Data Protection", the Agreement and the Privacy Policy of the website ${PRIVACY_POLICY_CONFIG.WEBPLATFORM_NAME} (${PRIVACY_POLICY_CONFIG.WEBSITE_URL}/privacy-policy).`,
            },
            {
              text: '6.2. The Executor has the right to transfer the Customer\'s personal data to third parties if such transfer is required for the Executor to fulfill its obligations under the Agreement and protect the rights and freedoms of the Customer or third parties if the Customer violates the terms of the Agreement.',
            },
          ],
        },
        {
          id: 'warranties',
          heading: '7. Warranties',
          content: [
            {
              text: '7.1. The Executor warrants that it has all the rights and authority necessary to conclude and perform this Agreement.',
            },
            {
              text: '7.2. By agreeing to and accepting the terms of this Offer by accepting it, the Customer certifies to the Executor and warrants to the Executor that:',
              list: [
                '7.2.1. The Customer provided accurate information when registering as a user on the website and when executing payment documents for services;',
                '7.2.2. The Customer enters into this Agreement voluntarily, and the Customer: a) has fully read the terms of the Offer, b) fully understands the subject matter of the Agreement (Offer);',
                '7.2.3. The Customer has all rights and authority necessary to conclude and perform the Agreement;',
                '7.2.4. The Customer has all intellectual property rights to objects placed by him on the website (marks for goods and services, copyright objects, industrial designs, etc.).',
              ],
            },
          ],
        },
        {
          id: 'claims-procedure',
          heading: '8. Претензійний порядок',
          content: [
            {
              text: '8.1. Усі суперечки та розбіжності Сторони намагаються вирішити шляхом переговорів.',
            },
            {
              text: `8.2. Претензія направляється у письмовій формі на контактну електронну пошту Виконавця: ${PRIVACY_POLICY_CONFIG.EMAIL}.`,
            },
            {
              text: '8.3. Строк розгляду претензії – 30 календарних днів з моменту її отримання.',
            },
            {
              text: '8.4. Якщо спір не вдається вирішити переговорами, він вирішується в судовому порядку відповідно до законодавства України.',
            },
          ],
        },
        {
          id: 'executor-data',
          heading: '9. Дані Виконавця',
          content: [
            {
              text: `Приватне підприємство "Інтернет-агентство "Нью Медіа Спейс"
Місцезнаходження: 76018, Івано-Франківськ, вул. Гузара Любомира Кардинала, 24Б, офіс 14
Код ЄДРПОУ 36498362

Контактна інформація: ${PRIVACY_POLICY_CONFIG.EMAIL}`,
            },
          ],
        },
        {
          id: 'term-termination',
          heading: '10. Строк дії Договору та умови розірвання',
          content: [
            {
              text: '10.1. Договір набирає чинності з моменту Акцепту та діє до повного виконання зобов\'язань сторонами.',
            },
            {
              text: '10.2. Замовник має право в будь-який момент припинити користування Послугами, видаливши свій акаунт.',
            },
            {
              text: '10.3. Виконавець має право розірвати Договір в односторонньому позасудовому порядку у разі грубого порушення Замовником умов Договору (п. 4.2), попередивши Замовника за 3 дні.',
            },
          ],
        },
      ],
    },
  };

  const currentContent = content[language as 'ua' | 'en'];

  return (
    <div className="public-offer-page">
      {/* Main Content */}
      <main className="offer-container">
        <div className="offer-content">
          {/* Title Section */}
          <div className="offer-title-section">
            <h1 className="offer-main-title">{currentContent.title}</h1>
          </div>

          {/* Introduction */}
          <section className="offer-section intro-section">
            <h2 className="offer-section-title">{currentContent.intro.heading}</h2>
            <p className="offer-text">
              {currentContent.intro.text.split('\n').map((paragraph, pIndex, arr) => (
                <span key={pIndex}>
                  {renderTextWithLinks(paragraph)}
                  {pIndex < arr.length - 1 && (
                    <>
                      <br />
                    </>
                  )}
                </span>
              ))}
            </p>
          </section>

          {/* Content Sections */}
          {currentContent.sections.map((section) => (
            <section key={section.id} id={section.id} className="offer-section">
              <h2 className="offer-section-title">{section.heading}</h2>

              {section.content.map((contentItem, contentIndex) => (
                <div key={contentIndex} className="offer-subsection">
                  {'text' in contentItem && contentItem.text && (
                    <p className="offer-text">
                      {contentItem.text.split('\n').map((paragraph, pIndex, arr) => (
                        <span key={pIndex}>
                          {renderTextWithLinks(paragraph)}
                          {pIndex < arr.length - 1 && (
                            <>
                              <br />
                            </>
                          )}
                        </span>
                      ))}
                    </p>
                  )}

                  {'list' in contentItem && contentItem.list && (
                    <ul className="offer-list">
                      {contentItem.list.map((item: string, itemIndex: number) => (
                        <li key={itemIndex} className="offer-list-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          ))}

          {/* Table of Contents */}
          <nav className="offer-toc">
            <h3 className="toc-title">{language === 'ua' ? 'Зміст' : 'Contents'}</h3>
            <ul className="toc-list">
              {currentContent.sections
                .filter((section) => {
                  // Only show main sections, not nested subsections
                  return section.heading.startsWith('Терміни та визначення') ||
                         section.heading.match(/^\d+\./);
                })
                .map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="toc-link"
                      onClick={handleTocClick(section.id)}
                    >
                      {section.heading}
                    </a>
                  </li>
                ))}
            </ul>
          </nav>
        </div>
      </main>

      {/* Scroll Button */}
      {showScrollButton && (
        <button 
          className="offer-scroll-button"
          onClick={handleScrollClick}
          title={language === 'ua' ? 'Вверх' : 'Up'}
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PublicOfferPage;
